import { useState, useMemo } from "react";
import FeetInchesInput from "../../components/FeetInchesInput";
import { dimText } from "../../utils/feetInches";
import { useUnits } from "../../utils/useUnits";
import { wallSystemTypes } from "../../utils/wallTypes";
import { RoomCriteriaExpand } from "./TakeoffCriteriaPanel";
import { computeBaselineRoomScope } from "../../config/roomScopeFormulas";
import { CostCodePicker } from "../../components/CostCodePicker";
import { PATTERN_OPTIONS, SYNC_FIELD_OPTIONS } from "../../config/conditionCalcTypes";
import { calcRoomSystems, parseFootCandles } from "../../utils/roomSystemsEngine";
import { effectiveCriterion } from "../../config/takeoffCriteria";
import {
  ELEMENT_DISCIPLINES, BASIS_BY_VALUE, inferElementBasis, inferElementDiscipline,
  isPlaceable, isEngineBasis, entityKeyOf, resolveElementQuantity, placedCountFor, roomAreasOf,
} from "../../config/roomElements";
import { parseElementSettings, quantityTrace, elementPickerPool, coveredBaselineKeys } from "../../config/roomElements";
import WallSystemsEditor, { inheritedLabels, wallSystemLabel } from "../../components/WallSystemsEditor";
import { backOutsForElement, BACK_OUT_BASES } from "../../utils/takeoffExclusions";
import { openingsForRoom } from "../../config/openings";
import { isProposedRoom, confidenceLabel, proposalDelta, describeChange } from "../../config/roomProposals";
import { governingLines } from "../../utils/layoutReview";

// Canvas-first room editing: click a room on the Layout Canvas, edit every
// attribute here instead of only in the schedule table. Same fields as the
// schedule table row, plus the same discipline-criteria drill-down
// (RoomCriteriaExpand) already used there — relocated into a flyout so
// estimators working the canvas don't have to switch views to fix a room.
const fld = { fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 3 };
const inp = { height: 32, width: "100%", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 13, color: "var(--text)", background: "var(--surface)", outline: "none", boxSizing: "border-box" };

const WIDTH_KEY = "cs-room-detail-flyout-width";
const MIN_WIDTH = 380;
const MAX_WIDTH = 900;

function loadWidth() {
  try {
    const v = +localStorage.getItem(WIDTH_KEY);
    if (v >= MIN_WIDTH && v <= MAX_WIDTH) return v;
  } catch { /* private window / blocked storage — fall back below */ }
  return 420;
}

export default function RoomDetailFlyout({
  room, zones, isLocked, layers = [],
  criteria, scopeByDiscipline, scopeExclusions,
  onSaveRoom, onSaveCriterion, onToggleRoomScope,
  roomScopeItems = [], onSaveRoomScopeItem, onDeleteRoomScopeItem,
  costCodes = [],
  roomTemplates = [], onChangeRoomTemplate,
  neighborWalls = [], // this room's joints with other rooms on its drawing (utils/neighborWalls.js) — the "Neighbors & walls" readout
  systemsCtx = null, // roomSystemsEngine.js context ({ bodAttrs, settings, tunables }) — enables the Engineering block below
  takeoffConditions = [], // this revision's placed conditions — the Engineering block's drawn-vs-effective count (autoLayoutEngine.js)
  rooms = [],             // every room in the revision — opening deductions (D-3) resolve a door's other room from wall adjacency
  onResetAutoLayout,      // (room, 'hepa'|'light') — "Regenerate layout"
  onDrawRoom,             // (room) — enter canvas draw mode targeting THIS room (place an unplaced room while the flyout stays open)
  isDrawingThis = false,  // the canvas is currently in draw mode for this room
  // Room Build-Up (db/70_room_elements.sql) — the estimator's per-room element
  // decisions, the catalog they're picked from, and the write/place handlers.
  roomElements = [],
  roomTemplateEquipment = [], // Room Templates' Standard Elements rows — element provenance ("from template X")
  catalog = { items: [], assemblies: [] },
  onSaveRoomElement,      // (payload without revision_id) — RoomTakeoff.jsx adds revision_id
  onDeleteRoomElement,    // (roomElementId)
  onPlaceElement,         // (element/entity) — enter canvas symbol-placement mode for its Item/Assembly
  onAddBackOut,           // (element) — enter canvas back-out draw mode for this element (Takeoff Canvas 2.0 D-2)
  onDeleteBackOut,        // (condition row) — removes one back-out (sp_SaveTakeoffConditionDrawn is_delete)
  drawingBackOutFor = null, // RoomElementID the canvas is currently tracing a back-out for
  onHighlight,            // ({ roomId, conditionIds }) — Track I: light up this quantity's geometry on the canvas
  onRejectProposal,       // (room) — Track G: reject an AI-proposed room (deletes it; it never existed as scope)
  layoutReview = null,    // Track H — layoutReview.reviewRoomLayout result for this room (governing logic, placed vs effective, conflicts)
  layoutReviewOn = false, // Track H — the canvas is drawing this room's footprints + conflicts
  onReviewLayout,         // (room) — toggle the canvas layout-review overlay for this room
  onClose,
}) {
  const units = useUnits();   // the viewer's display units (feet-inches, or metric) — a hook, so it stays above every early return
  const [width, setWidth] = useState(loadWidth);

  // Drag the left edge to resize. Native listeners (not React's onPointerMove)
  // so dragging still tracks smoothly if the pointer leaves the thin handle.
  function startResize(e) {
    e.preventDefault();
    const startX = e.clientX, startWidth = width;
    function onMove(ev) {
      const w = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (startX - ev.clientX)));
      setWidth(w);
    }
    function onUp() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setWidth(w => { try { localStorage.setItem(WIDTH_KEY, String(w)); } catch { /* ignore */ } return w; });
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  if (!room) return null;

  const set = (patch) => onSaveRoom({ ...room, ...patch });

  const placed = room.CanvasX != null && room.CanvasX !== "" && +room.LengthFt > 0 && +room.WidthFt > 0;

  // Header strip — the room's criteria at a glance (framework doc §17):
  // "719 SF | 10' | ISO 8 | 45 ACH | 100 fc".
  const hdrAreas = roomAreasOf(room);
  const hdrFc = parseFootCandles(effectiveCriterion(criteria || {}, "elec", "light_level", room).value);
  const hdrTemplate = roomTemplates.find(t => t.RoomTemplateID === room.RoomTemplateID)?.Name;
  const headerBits = [
    hdrAreas.floorSF > 0 ? `${Math.round(hdrAreas.floorSF).toLocaleString()} SF` : "not placed",
    room.HeightFt ? `${room.HeightFt}′` : null,
    hdrTemplate || null,
    room.ACH ? `${room.ACH} ACH` : null,
    hdrFc ? `${hdrFc} fc` : null,
  ].filter(Boolean);

  // Non-modal on purpose (2026-09-11, per direct feedback): no backdrop, so
  // the canvas stays fully interactive while this is open — set the room up
  // here and trace it on the canvas in parallel. Close via ✕ (or Esc when no
  // canvas tool is active — see TakeoffCanvas's key handler).
  return (
    <>
      <div style={{
        position: "fixed", top: 0, right: 0, width, height: "100vh",
        background: "var(--surface)", borderLeft: "1px solid var(--cs-border)",
        boxShadow: "-4px 0 32px rgba(0,0,0,0.12)", zIndex: 300,
        display: "flex", flexDirection: "column",
      }}>
        <div
          onPointerDown={startResize}
          title="Drag to resize"
          style={{ position: "absolute", top: 0, left: -3, width: 7, height: "100%", cursor: "col-resize", zIndex: 301 }}
        />
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cs-border)", flexShrink: 0, background: "#0c4a6e", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>📋 {room.RoomNumber ? `${room.RoomNumber} — ` : ""}{room.RoomName}</div>
            <div style={{ fontSize: 12, color: "#93c5fd", marginTop: 3 }}>{headerBits.join(" · ")}</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", cursor: "pointer", fontSize: 18, color: "#fff", lineHeight: 1, padding: "4px 8px", borderRadius: 6 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            {isProposedRoom(room) && (() => {
              // Track G — the room came from the AI prefill and is still a proposal.
              // High confidence is not approval: the estimator accepts, edits or rejects.
              const conf = confidenceLabel(room.DetectionConfidence);
              const delta = proposalDelta(room);
              return (
                <div style={{ border: "1px solid #fcd34d", background: "var(--tint-amber)", borderRadius: "var(--radius)", padding: "8px 10px", fontSize: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ color: "#92400e" }}>🤖 AI proposal</strong>
                    <span style={{ color: "var(--text2)" }}>{conf.label}{room.DetectionSource ? ` · ${room.DetectionSource.replace("_", " ")}` : ""}</span>
                    {!isLocked && (
                      <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                        <button onClick={() => set({ DetectionStatus: "accepted" })} style={{ ...smallBtn, background: "var(--tint-green)", color: "#166534", borderColor: "#86efac" }} title="Accept this room as scope (recorded with who and when). Edits you already made are kept.">✓ Accept</button>
                        {onRejectProposal && <button onClick={() => { if (window.confirm(`Reject and remove "${room.RoomName}"? It was proposed by the AI and has not been accepted.`)) onRejectProposal(room); }} style={{ ...smallBtn, background: "var(--tint-red)", color: "#991b1b", borderColor: "#fca5a5" }} title="Remove the proposed room">✕ Reject</button>}
                      </span>
                    )}
                  </div>
                  {delta.edited && <div style={{ marginTop: 4, color: "var(--text2)" }}>Edited since the proposal: {delta.changes.map(describeChange).join(" · ")}</div>}
                  {!delta.edited && <div style={{ marginTop: 4, color: "var(--text3)" }}>Exactly as proposed — check the outline and label against the drawing before accepting.</div>}
                </div>
              );
            })()}
            <div>
              <label style={fld}>Room Name</label>
              {/* Uncontrolled inputs (defaultValue) so typing doesn't re-render the
                  canvas on every keystroke — but keyed on the stored value so a
                  change that arrives from the canvas (a drawing filling in L/W,
                  a template setting ACH) remounts the field with the new value
                  instead of leaving a stale one on screen. */}
              <input
                key={`name-${room.RoomName}`}
                defaultValue={room.RoomName}
                disabled={isLocked}
                style={inp}
                onBlur={e => { const v = e.target.value.trim(); if (v && v !== room.RoomName) set({ RoomName: v }); else e.target.value = room.RoomName; }}
              />
            </div>
            {onChangeRoomTemplate && (
              <div>
                <label style={fld}>Room Template</label>
                <select
                  value={room.RoomTemplateID ?? ""}
                  disabled={isLocked}
                  title="Standard room template (Setup > Estimate Engine > Rooms) — sets ACH/classification once, and feeds discipline-criteria defaults for any field this room hasn't overridden itself"
                  onChange={e => onChangeRoomTemplate(room, e.target.value ? +e.target.value : null)}
                  style={{ ...inp, color: room.RoomTemplateID ? "var(--text)" : "var(--text3)" }}
                >
                  <option value="">— none —</option>
                  {roomTemplates.filter(t => t.IsActive || t.RoomTemplateID === room.RoomTemplateID).map(t => (
                    <option key={t.RoomTemplateID} value={t.RoomTemplateID}>{t.Name}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Wall system — what this room's walls are built from. Inherits
                the template's default; pick another catalog wall type to
                substitute for THIS room (e.g. gypsum in an office). The
                boundary engine fills still-unassigned walls from it. */}
            <WallSystemField room={room} roomTemplates={roomTemplates} catalog={catalog} layers={layers} isLocked={isLocked} onSet={set} neighborWalls={neighborWalls} />
            {/* Wall system field ends */}
            <div style={{ display: "flex", gap: 10 }}>
              {["LengthFt", "WidthFt", "HeightFt"].map(k => (
                <div key={k} style={{ flex: 1 }}>
                  <label style={fld}>{k === "LengthFt" ? "L" : k === "WidthFt" ? "W" : "H"} <span style={{ textTransform: "none", fontWeight: 500 }}>(ft-in)</span></label>
                  <FeetInchesInput valueFt={room[k]} units={units} disabled={isLocked} ariaLabel={k === "LengthFt" ? "Length" : k === "WidthFt" ? "Width" : "Height"}
                    style={{ ...inp, textAlign: "right" }} onCommit={v => set({ [k]: v })} />
                </div>
              ))}
            </div>

            {/* Placement — where the room is on the canvas. An unplaced room can
                be drawn from here without closing the flyout; the drawing fills
                L/W (and PolygonJson for a traced outline) and the fields above
                refresh in place. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: "var(--radius)", background: isDrawingThis ? "var(--tint-blue)" : "var(--surface2, #f8fafc)", border: `1px solid ${isDrawingThis ? "#93c5fd" : "var(--cs-border)"}` }}>
              <div style={{ flex: 1, fontSize: 12 }}>
                {isDrawingThis ? (
                  <span style={{ color: "#1d4ed8", fontWeight: 600 }}>✏️ Drawing on the canvas — drag a box or click each corner; Enter/double-click to finish, Esc to cancel.</span>
                ) : placed ? (
                  <span style={{ color: "var(--text2)" }}>Placed on canvas · {dimText(room.LengthFt, units)} × {dimText(room.WidthFt, units)}{room.PolygonJson ? " · traced outline" : ""}</span>
                ) : (
                  <span style={{ color: "#b45309", fontWeight: 600 }}>Not placed on the canvas yet.</span>
                )}
              </div>
              {!isLocked && onDrawRoom && !isDrawingThis && (
                <button
                  onClick={() => onDrawRoom(room)}
                  title={placed ? "Re-trace this room's outline on the canvas (replaces its current placement)" : "Enter draw mode for this room — the flyout stays open while you trace it"}
                  style={{ fontSize: 11.5, fontWeight: 700, padding: "4px 10px", border: "1px solid #2563eb", background: "#2563eb", color: "#fff", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  ✏️ {placed ? "Redraw" : "Draw this room"}
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={fld}>ACH</label>
                <input
                  key={`ach-${room.ACH ?? ""}`}
                  type="number" min={0}
                  defaultValue={room.ACH ?? ""}
                  disabled={isLocked}
                  placeholder="—"
                  style={{ ...inp, textAlign: "right" }}
                  onBlur={e => { const v = e.target.value === "" ? null : +e.target.value; if (v !== (room.ACH ?? null)) set({ ACH: v }); }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={fld}>Pressure Target</label>
                <input
                  key={`pt-${room.PressureTarget ?? ""}`}
                  defaultValue={room.PressureTarget ?? ""}
                  disabled={isLocked}
                  placeholder="—"
                  style={inp}
                  onBlur={e => { const v = e.target.value.trim() || null; if (v !== (room.PressureTarget ?? null)) set({ PressureTarget: v }); }}
                />
              </div>
            </div>
            <div>
              <label style={fld}>AHU Zone</label>
              <select
                value={room.AHUZoneID ?? ""}
                disabled={isLocked}
                onChange={e => set({ AHUZoneID: e.target.value ? +e.target.value : null })}
                style={inp}
              >
                <option value="">— unassigned —</option>
                {zones.map(z => <option key={z.AHUZoneID} value={z.AHUZoneID}>{z.ZoneName}</option>)}
              </select>
            </div>
          </div>

          {/* ROOM BUILD-UP — the primary scope interface (framework doc §3/§17):
              what the estimator has added to this room, by discipline, each
              element showing its EFFECTIVE quantity first. Engines only run
              for systems that have an element here. */}
          <RoomBuildUp
            room={room}
            criteria={criteria}
            systemsCtx={systemsCtx}
            isLocked={isLocked}
            roomElements={roomElements}
            roomTemplates={roomTemplates}
            roomTemplateEquipment={roomTemplateEquipment}
            catalog={catalog}
            takeoffConditions={takeoffConditions}
            rooms={rooms}
            onAddBackOut={onAddBackOut}
            onDeleteBackOut={onDeleteBackOut}
            drawingBackOutFor={drawingBackOutFor}
            onHighlight={onHighlight}
            layoutReview={layoutReview}
            layoutReviewOn={layoutReviewOn}
            onReviewLayout={onReviewLayout}
            onSaveRoomElement={onSaveRoomElement}
            onDeleteRoomElement={onDeleteRoomElement}
            onPlaceElement={onPlaceElement}
            onResetAutoLayout={onResetAutoLayout}
            onSaveCriterion={onSaveCriterion}
          />

          <Collapsible title="Design criteria" defaultOpen={false}
            subtitle="What the room must achieve — the inputs the engines read (ACH, light level, filter and fixture types, occupancy…). Not estimate lines.">
            <RoomCriteriaExpand
              room={room}
              criteria={criteria}
              scopeByDiscipline={scopeByDiscipline}
              scopeExclusions={scopeExclusions}
              isLocked={isLocked}
              onSave={onSaveCriterion}
              onToggleScope={onToggleRoomScope}
            />
          </Collapsible>

          <Collapsible title="Derived architectural quantities" defaultOpen={false}
            subtitle="Preliminary scope lines derived from L/W/H and criteria — technical detail; Room Build-Up above is the primary scope list.">
            <ArchitecturalScopeList
              room={room}
              criteria={criteria}
              roomScopeItems={roomScopeItems}
              roomElements={roomElements}
              costCodes={costCodes}
              isLocked={isLocked}
              onSave={onSaveRoomScopeItem}
              onDelete={onDeleteRoomScopeItem}
            />
          </Collapsible>
        </div>
      </div>
    </>
  );
}

const cellInp = { width: "100%", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 6px", background: "var(--surface)", color: "var(--text)", boxSizing: "border-box" };

// ── Engineering (HEPA + Lighting) ──────────────────────────────────────────────
// The calculation basis for this room's HEPA and light-fixture counts
// (src/utils/roomSystemsEngine.js), shown as Calculated / Override / Effective
// so the estimate can explain why a quantity exists — and the ONE place an
// estimator sets an explicit override (written as the room's own criteria
// row through the same onSaveCriterion path as every other per-room field;
// blank = back to calculated). Nothing here is persisted except the override.
const engRow = { display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, padding: "2px 0" };
const engLbl = { color: "var(--text3)" };
const engVal = { fontVariantNumeric: "tabular-nums", fontWeight: 600, textAlign: "right" };
const engInp = { ...cellInp, width: 90, textAlign: "right" };
const fmt0 = (n) => n == null || !Number.isFinite(n) ? "—" : Math.round(n).toLocaleString();
const fmt1 = (n) => n == null || !Number.isFinite(n) ? "—" : (Math.round(n * 10) / 10).toLocaleString();

function EngRow({ label, value, hint, strong }) {
  return (
    <div style={engRow} title={hint || ""}>
      <span style={engLbl}>{label}</span>
      <span style={{ ...engVal, fontWeight: strong ? 800 : 600 }}>{value}</span>
    </div>
  );
}

function OverrideRow({ label, storedValue, calculated, disabled, onCommit }) {
  return (
    <div style={{ ...engRow, alignItems: "center" }}>
      <span style={engLbl}>{label}</span>
      <input
        key={storedValue ?? ""}
        type="number" min={0} step={1}
        defaultValue={storedValue ?? ""}
        disabled={disabled}
        placeholder={calculated != null ? `calc ${calculated}` : "—"}
        title="Explicit estimator override. Blank = use the calculated requirement."
        style={engInp}
        onBlur={e => {
          const v = e.target.value.trim();
          const next = v === "" ? "" : String(Math.max(0, Math.round(+v)));
          if (next !== (storedValue ?? "")) onCommit(next);
        }}
      />
    </div>
  );
}

// Track H — the governing logic behind an engine element's calculated count:
// both constraints the engine evaluated, the one that won in bold, and the
// calculated → override → effective chain in one line. Reads the engine's
// own result (roomSystemsEngine) — nothing is recomputed here.
function GoverningRow({ system, sys }) {
  const g = governingLines(system, sys);
  if (!g.lines.length) return null;
  return (
    <div style={{ marginTop: 4, fontSize: 11, color: "var(--text3)", borderLeft: "2px solid var(--cs-border)", paddingLeft: 8 }} title="Which constraint sizes this room, from the engine's own numbers">
      {g.lines.map(l => (
        <div key={l.key} style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          <span style={{ width: 12, textAlign: "center", color: l.governs ? "#15803d" : "var(--text3)" }}>{l.governs ? "▶" : "·"}</span>
          <span style={{ fontWeight: l.governs ? 700 : 400, color: l.governs ? "var(--text)" : "var(--text3)" }}>{l.text}</span>
        </div>
      ))}
      <div style={{ marginTop: 2, color: "var(--text2)" }}>{g.summary}</div>
    </div>
  );
}

// Track H — layout review for one engine system: conflicts the review found
// on this room's footprints (click one to light the rows up) and the toggle
// that draws every footprint + conflict on the canvas. Nothing is moved.
function LayoutReviewRow({ system, review, on, onToggle, onHighlight, room }) {
  const r = review.systems[system];
  const mine = review.conflicts.filter(c => c.system === system || c.system === "mixed");
  return (
    <div style={{ marginTop: 4, fontSize: 11 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span style={{ color: mine.some(c => c.severity === "warn") ? "#b45309" : mine.length ? "var(--text2)" : "#15803d", fontWeight: 600 }}>
          {mine.length ? `${mine.filter(c => c.severity === "warn").length} layout conflict${mine.filter(c => c.severity === "warn").length === 1 ? "" : "s"} · ${mine.filter(c => c.severity === "info").length} note${mine.filter(c => c.severity === "info").length === 1 ? "" : "s"}` : r.placed ? "Layout clear — inside the room, no overlaps" : "Nothing placed yet"}
        </span>
        {r.locked > 0 && <span style={{ color: "var(--text3)" }} title="Dragged symbols keep their position through every regenerate">{r.locked} moved by hand</span>}
        {onToggle && (
          <button onClick={onToggle} style={{ marginLeft: "auto", fontSize: 10.5, padding: "1px 7px", border: `1px solid ${on ? "#16a34a" : "var(--cs-border)"}`, background: on ? "var(--tint-green)" : "var(--surface)", color: on ? "#166534" : "var(--text2)", borderRadius: 4, cursor: "pointer" }}
            title="Draw every HEPA / light footprint in this room on the canvas — green clear, red in a conflict — with the lighting spacing limit around each light">
            🔍 {on ? "Hide layout review" : "Review layout"}
          </button>
        )}
      </div>
      {mine.slice(0, 6).map((c, i) => (
        <div key={i} onClick={() => onHighlight?.({ roomId: room.RoomID, conditionIds: c.conditionIds || [] })} style={{ display: "flex", gap: 5, color: "var(--text2)", cursor: onHighlight ? "pointer" : "default", padding: "1px 0" }} title="Click to highlight on the canvas">
          <span style={{ color: c.severity === "warn" ? "#b45309" : "var(--text3)" }}>{c.severity === "warn" ? "⚠" : "ℹ"}</span><span>{c.message}</span>
        </div>
      ))}
      {mine.length > 6 && <div style={{ color: "var(--text3)" }}>… {mine.length - 6} more in the Review queue</div>}
    </div>
  );
}

// Drawn vs. effective (Phase 5): what's actually on the canvas for a system
// (engine-generated + hand-placed symbols of an Item tagged with that
// system) against the engineering requirement. The two can legitimately
// differ after an estimator deletes or adds symbols — that difference is
// shown, never silently reconciled, and "Adopt drawn count" writes it as an
// explicit override through the same criterion path as the override input.
function DrawnRow({ label, drawn, autoCount, effective, disabled, onAdopt, onRegenerate }) {
  const differs = drawn !== effective;
  return (
    <div style={{ ...engRow, alignItems: "center", flexWrap: "wrap" }}>
      <span style={engLbl}>{label}</span>
      <span style={{ ...engVal, color: differs && (drawn > 0 || autoCount > 0) ? "#b45309" : undefined }} title={`${autoCount} engine-generated · ${drawn - autoCount} hand-placed`}>
        {drawn}{autoCount ? <span style={{ fontWeight: 400, color: "var(--text3)" }}> ({autoCount} auto)</span> : null}
      </span>
      <span style={{ display: "flex", gap: 6, width: "100%", justifyContent: "flex-end", marginTop: 2 }}>
        {differs && drawn > 0 && !disabled && (
          <button onClick={() => onAdopt(drawn)} title="Record the drawn count as this room's explicit override — the engine's calculation stays visible above it."
            style={{ fontSize: 10.5, padding: "1px 7px", border: "1px solid #f59e0b", background: "var(--tint-amber)", color: "#92400e", borderRadius: 4, cursor: "pointer" }}>
            Adopt drawn {drawn} as override
          </button>
        )}
        {!disabled && onRegenerate && (
          <button onClick={onRegenerate} title="Delete this room's generated symbols for this system and lay them out again from the effective count (dragged positions are reset)."
            style={{ fontSize: 10.5, padding: "1px 7px", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text2)", borderRadius: 4, cursor: "pointer" }}>
            ↺ Regenerate layout
          </button>
        )}
      </span>
    </div>
  );
}

// Engineering detail rows for an engine-driven element card (framework doc
// §7/§8: "secondary to the main result") — the diagnostics behind a HEPA or
// lighting count. Override/effective/canvas rows live on the card itself.
function HepaEngineeringDetails({ hepa, systems }) {
  const g = systems.geometry;
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>
        {fmt0(g.floorAreaSf)} SF × {fmt1(g.H)} ft = {fmt0(g.volumeCf)} CF · ACH {fmt1(systems.hvac.targetAch)} → <strong>{fmt0(hepa.requiredCfm)} CFM</strong>
      </div>
      <EngRow label="Filter" value={hepa.filterType || "— none selected —"} hint="Terminal filtration criterion (Design criteria below)" />
      <EngRow label="Design cfm / unit" value={`${fmt0(hepa.designAirflowPerUnit)}${hepa.designAirflowSource === "default" ? " (default)" : ""}`}
        hint={hepa.designAirflowSource === "default" ? "No Design airflow on the selected Terminal filtration option — using the Basis of Design default." : "From the selected Terminal filtration option (Basis of Design)."} />
      <EngRow label="By airflow" value={fmt0(hepa.countByAirflow)} hint={`CEILING(${fmt0(hepa.requiredCfm)} / ${fmt0(hepa.designAirflowPerUnit)})`} />
      <EngRow label={`By coverage (${fmt1(hepa.coveragePercent)}% × ${fmt1(hepa.faceAreaSf)} SF)`} value={hepa.coveragePercent > 0 ? fmt0(hepa.countByCoverage) : "—"}
        hint={hepa.coveragePercent > 0 ? `CEILING(${fmt0(g.floorAreaSf)} × ${fmt1(hepa.coveragePercent)}% / ${fmt1(hepa.faceAreaSf)})` : "Set HEPA ceiling coverage (%) under Design criteria ▸ Mechanical to apply this constraint."} />
      <EngRow label="Actual cfm / unit" value={fmt0(hepa.actualAirflowPerUnit)} />
      <EngRow label="Face velocity" value={`${fmt0(hepa.faceVelocityFpm)} fpm`} />
      <EngRow label="Ceiling coverage" value={`${fmt1(hepa.actualCoveragePercent)}%`} />
    </div>
  );
}

function LightingEngineeringDetails({ lighting, systems }) {
  const g = systems.geometry;
  return (
    <div style={{ marginTop: 4 }}>
      <EngRow label="Fixture" value={lighting.fixtureType || "— none selected —"} hint="Lighting fixtures criterion (Design criteria below)" />
      <EngRow label="Target" value={lighting.targetFc ? `${lighting.targetFc} fc` : "—"} />
      <EngRow label="Required lumens" value={fmt0(lighting.requiredLumens)} hint={`${fmt0(g.floorAreaSf)} SF × ${lighting.targetFc ?? "—"} fc`} />
      <EngRow label={`By lumens (${fmt0(lighting.fixtureLumens)} lm × CU ${lighting.coefficientOfUtilization} × LLF ${lighting.lightLossFactor})`}
        value={lighting.countByLumens != null ? fmt0(lighting.countByLumens) : "—"}
        hint={lighting.fixtureLumens ? "" : "Selected fixture has no lumen rating in Basis of Design — no engineering basis; using the template/project default."} />
      <EngRow label={`By spacing (max ${fmt1(lighting.maxSpacingFt)} ft)`} value={lighting.countBySpacing != null ? fmt0(lighting.countBySpacing) : "—"}
        hint={`Spacing criterion ${lighting.spacingCriterion}${lighting.spacingSource === "default" ? " (default)" : ""} × mounting height ${fmt1(lighting.mountingHeightFt)} ft`} />
      <EngRow label="Load" value={`${fmt0(lighting.totalWatts)} W · ${fmt0(lighting.totalVa)} VA`}
        hint={`${lighting.vaPerFixture} VA/fixture (${lighting.vaSource === "settings" ? "Electrical settings fallback" : lighting.vaSource === "default" ? "built-in default" : "selected fixture"})`} />
    </div>
  );
}

// ── Room Build-Up ───────────────────────────────────────────────────────────────
// Framework doc (2026-09-11): the estimator builds the room by adding
// physical systems/components (elements) by discipline; the engines decide
// how much. Element = a catalog Item/Assembly flagged "Room element"
// (Setup ▸ Estimate Engine); rows persist in RoomElements (db/70).
const smallBtn = { fontSize: 10.5, padding: "1px 7px", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text2)", borderRadius: 4, cursor: "pointer", whiteSpace: "nowrap" };
const primaryBtn = { fontSize: 11.5, fontWeight: 700, padding: "4px 10px", border: "1px solid #2563eb", background: "#2563eb", color: "#fff", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" };
const linkBtn = { fontSize: 11.5, background: "none", border: "none", color: "var(--cs-blue)", cursor: "pointer", padding: "2px 4px" };
const warnStyle = { fontSize: 11, color: "#92400e", background: "var(--tint-amber)", border: "1px solid #fde68a", borderRadius: 5, padding: "3px 8px" };
const disciplineOf = (e) => e.Discipline || inferElementDiscipline(e) || "other";
const fmtQty = (v, unit) => v == null || !Number.isFinite(+v) ? "—" : (+v).toLocaleString(undefined, { maximumFractionDigits: unit === "ea" ? 0 : 1 });

function Collapsible({ title, subtitle, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: "1px solid var(--cs-border)" }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: "10px 20px 6px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.1s" }}>▾</span>{title}
      </div>
      {open && subtitle && <div style={{ padding: "0 20px 6px", fontSize: 11, color: "var(--text3)" }}>{subtitle}</div>}
      {open && children}
    </div>
  );
}

function RoomBuildUp({ room, criteria, systemsCtx, isLocked, roomElements, roomTemplates = [], roomTemplateEquipment = [], catalog, takeoffConditions, onSaveRoomElement, onDeleteRoomElement, onPlaceElement, onResetAutoLayout, onSaveCriterion,
  // threaded through from the flyout for the element cards (D-2 back-outs, Track I highlight, Track H layout review) — every one is read at render, so a missing prop is a ReferenceError, not a no-op
  rooms = [], onAddBackOut, onDeleteBackOut, drawingBackOutFor = null, onHighlight, layoutReview = null, layoutReviewOn = false, onReviewLayout }) {
  // Gated systems: only elements present on this room make their engine run.
  const systems = useMemo(() => calcRoomSystems(room, criteria || {}, systemsCtx || {}), [room, criteria, systemsCtx]);
  const mine = useMemo(() => roomElements.filter(e => e.RoomID === room.RoomID), [roomElements, room.RoomID]);
  const [addingTo, setAddingTo] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const canEdit = !isLocked && typeof onSaveRoomElement === "function";
  const groups = [...ELEMENT_DISCIPLINES, { key: "other", label: "Unassigned discipline", icon: "•" }];

  return (
    <div style={{ borderTop: "1px solid var(--cs-border)" }}>
      <div style={{ padding: "10px 20px 4px", display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Room Build-Up</span>
        <span style={{ fontSize: 11, color: "var(--text3)" }}>{mine.length} element{mine.length === 1 ? "" : "s"} · you decide what's in the room, the engine decides how much</span>
      </div>
      {groups.map(d => {
        const els = mine.filter(e => disciplineOf(e) === d.key);
        if (d.key === "other" && !els.length) return null;
        const isCollapsed = !!collapsed[d.key];
        const warnCount = els.reduce((n, el) => n + resolveElementQuantity({ element: el, room, systems, placed: placedCountFor(el, room.RoomID, takeoffConditions), backOuts: backOutsForElement(el, takeoffConditions), openings: openingsForRoom(room, takeoffConditions, rooms), formulas: catalog?.formulas, criteria, ctx: systemsCtx }).warnings.length, 0);
        return (
          <div key={d.key} style={{ padding: "4px 20px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span onClick={() => setCollapsed(c => ({ ...c, [d.key]: !c[d.key] }))} style={{ cursor: "pointer", flex: 1, display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700 }}>
                <span style={{ display: "inline-block", transform: isCollapsed ? "rotate(-90deg)" : "none", transition: "transform 0.1s", color: "var(--text3)" }}>▾</span>
                {d.icon} {d.label}
                <span style={{ fontWeight: 500, color: "var(--text3)", fontSize: 11 }}>{els.length} element{els.length === 1 ? "" : "s"}</span>
                {warnCount > 0 && <span style={{ fontSize: 11, color: "#b45309", fontWeight: 600 }}>⚠ {warnCount} need{warnCount === 1 ? "s" : ""} review</span>}
              </span>
              {canEdit && d.key !== "other" && addingTo !== d.key && (
                <button onClick={() => { setAddingTo(d.key); setCollapsed(c => ({ ...c, [d.key]: false })); }} style={{ ...linkBtn, fontWeight: 700 }}>+ Add Element</button>
              )}
            </div>
            {!isCollapsed && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                {addingTo === d.key && (
                  <AddElementPanel
                    discipline={d} room={room} criteria={criteria} systemsCtx={systemsCtx} catalog={catalog} existing={mine}
                    canPlace={typeof onPlaceElement === "function"}
                    onAdd={payload => { onSaveRoomElement(payload); setAddingTo(null); }}
                    onAddAndPlace={(payload, entity) => { onSaveRoomElement(payload); setAddingTo(null); onPlaceElement?.(entity); }}
                    onCancel={() => setAddingTo(null)}
                  />
                )}
                {els.map(el => (
                  <ElementCard key={el.RoomElementID} el={el} room={room} systems={systems} systemsCtx={systemsCtx} formulas={catalog?.formulas || []} criteria={criteria} takeoffConditions={takeoffConditions}
                    roomTemplates={roomTemplates} roomTemplateEquipment={roomTemplateEquipment}
                    canEdit={canEdit} onSave={onSaveRoomElement} onDelete={onDeleteRoomElement} onPlace={onPlaceElement}
                    onAddBackOut={onAddBackOut} onDeleteBackOut={onDeleteBackOut} drawingBackOut={drawingBackOutFor === el.RoomElementID} rooms={rooms} onHighlight={onHighlight}
                    onResetAutoLayout={onResetAutoLayout} onSaveCriterion={onSaveCriterion}
                    layoutReview={layoutReview} layoutReviewOn={layoutReviewOn} onReviewLayout={onReviewLayout} />
                ))}
                {!els.length && addingTo !== d.key && (
                  <div style={{ fontSize: 11.5, color: "var(--text3)", padding: "0 0 2px 18px" }}>Nothing added — this room has no {d.label.toLowerCase()} scope yet.</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Wall system select: "Template default — <name>" (blank value) or any
// linear-style catalog Item/Assembly as this room's substitution. Writes
// WallKind/WallItemID/WallAssemblyID through the normal room save.
function WallSystemField({ room, roomTemplates = [], catalog, layers = [], isLocked, onSet, neighborWalls = [] }) {
  const linear = [
    ...wallSystemTypes(catalog?.items || [], layers).map(i => ({ key: `item:${i.ItemID}`, kind: "item", id: i.ItemID, label: `${i.Code ? `${i.Code} — ` : ""}${i.ItemName}` })),
    ...wallSystemTypes(catalog?.assemblies || [], layers).map(a => ({ key: `assembly:${a.AssemblyID}`, kind: "assembly", id: a.AssemblyID, label: `${a.Code ? `${a.Code} — ` : ""}${a.Description}${" · assembly"}` })),
  ];
  const template = roomTemplates.find(t => t.RoomTemplateID === room.RoomTemplateID);
  const templateDefault = template?.WallKind ? (template.WallName || template.WallCode || "template wall system") : null;
  const own = room.WallKind && (room.WallKind === "item" ? room.WallItemID : room.WallAssemblyID) ? `${room.WallKind}:${room.WallKind === "item" ? room.WallItemID : room.WallAssemblyID}` : "";
  if (!linear.length && !own) return null; // nothing to pick from yet — no linear (wall) catalog rows
  const templatesById = new Map(roomTemplates.map(t => [t.RoomTemplateID, t]));
  return (
    <div>
      <label style={fld}>Wall system — all walls</label>
      <select
        value={own} disabled={isLocked}
        title="What this room's walls are built from. Inherits the Room Template's default; pick another to substitute for this room only. The boundary engine fills this room's still-unassigned walls from it (a shared wall only when both rooms agree)."
        onChange={e => {
          const v = e.target.value;
          if (!v) return onSet({ WallKind: null, WallItemID: null, WallAssemblyID: null });
          const [kind, id] = v.split(":");
          onSet({ WallKind: kind, WallItemID: kind === "item" ? +id : null, WallAssemblyID: kind === "assembly" ? +id : null });
        }}
        style={{ ...inp, color: own ? "var(--text)" : "var(--text3)" }}
      >
        <option value="">{templateDefault ? `Template default — ${templateDefault}` : template ? "Template default — none set" : "— none —"}</option>
        {linear.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {own && templateDefault && <div style={{ fontSize: 10.5, color: "#b45309", marginTop: 2 }}>Substituted for this room (template default: {templateDefault}).</div>}
      <div style={{ marginTop: 8 }}>
        <label style={fld} title="Different wall systems for different kinds of wall in this room. A blank row inherits; Construction Standards pair rules still win over all of these.">Wall system — by position</label>
        <WallSystemsEditor
          value={room.WallSystemsJson}
          options={linear}
          disabled={isLocked}
          inherited={inheritedLabels({ room, templatesById })}
          onChange={json => onSet({ WallSystemsJson: json })}
        />
      </div>
      <NeighborWallsList neighborWalls={neighborWalls} catalog={catalog} roomTemplates={roomTemplates} />
    </div>
  );
}

// "Neighbors & walls": each wall this room shares with another room on its drawing — what the engine
// would put on it and why (a Construction Standards rule, a wall system, or nothing and the reason).
// Read-only; the rules themselves are edited in Setup ▸ Construction Standards.
function NeighborWallsList({ neighborWalls = [], catalog, roomTemplates = [] }) {
  if (!neighborWalls.length) return null;
  const tplName = (id) => roomTemplates.find(t => t.RoomTemplateID === id)?.Name || "no template";
  const sourceText = (f) => f.source === "rule" ? "Construction Standards rule" : f.source === "learned" ? `learned from ${f.learned?.samples ?? "past"} similar walls, ${Math.round((f.learned?.agreement ?? 0) * 100)}% agreement — unreviewed` : "wall system";
  return (
    <div style={{ marginTop: 10 }}>
      <label style={fld} title="Read-only. What the auto-wall fill would put on each wall this room shares with a neighbor. Rules are edited in Setup ▸ Construction Standards; the choice for a blank wall is made in Review Walls on the canvas.">Neighbors &amp; walls</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {neighborWalls.map(n => (
          <div key={n.key} style={{ fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 6, padding: "5px 8px", background: "var(--surface)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span><strong>{n.otherName}</strong> <span style={{ color: "var(--text3)" }}>({tplName(n.otherTemplateId)})</span></span>
              <span style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>{n.type === "shared" ? "shared wall" : "cavity"} · {n.overlapFt != null ? `${(+n.overlapFt).toFixed(1)}′` : ""}</span>
            </div>
            {n.fill
              ? <div style={{ color: "#166534" }}>→ {wallSystemLabel(n.fill, catalog)} <span style={{ color: "var(--text3)" }}>({sourceText(n.fill)})</span></div>
              : <div style={{ color: "#b45309" }}>→ left blank — {n.reason}</div>}
            {n.rules.length > 0 && (
              <div style={{ color: "var(--text3)" }}>
                {n.rules.length} matching rule{n.rules.length === 1 ? "" : "s"}: {n.rules.map(r => wallSystemLabel(r, catalog)).join("; ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Searchable element catalog for one discipline, then the element's quantity
// method and a live preview (ungated, so a HEPA element previews its count
// before it exists). "Add & Place" adds and drops into canvas symbol mode.
function AddElementPanel({ discipline, room, criteria, systemsCtx, catalog, existing, canPlace, onAdd, onAddAndPlace, onCancel }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [method, setMethod] = useState(null);
  const [fixedQty, setFixedQty] = useState("1");
  const formulas = catalog?.formulas || [];
  const [formulaId, setFormulaId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const all = useMemo(() => [
    ...(catalog?.items || []).map(i => ({ ...i, Kind: "item", Name: i.ItemName })),
    ...(catalog?.assemblies || []).map(a => ({ ...a, Kind: "assembly", Name: a.Description })),
  ], [catalog]);
  const flaggedAny = useMemo(() => all.some(e => e.IsRoomElement), [all]);
  const pool = useMemo(() => elementPickerPool(all, discipline.key, { showAll }), [all, discipline.key, showAll]);
  const existingKeys = useMemo(() => new Set(existing.map(entityKeyOf)), [existing]);
  const q = query.trim().toLowerCase();
  // A search runs over the whole pool; the cap only keeps a full-catalog list short.
  const candidates = pool.filter(e => {
    if (existingKeys.has(entityKeyOf(e))) return false;
    return !q || `${e.Code || ""} ${e.Name || ""}`.toLowerCase().includes(q);
  }).slice(0, 40);

  const basis = picked ? inferElementBasis(picked) : null;
  const placeable = picked ? isPlaceable(picked) : false;
  const engine = isEngineBasis(basis);
  const methods = !picked ? []
    : engine ? [["calculated", `Calculated by the ${basis === "engine_hepa" ? "HEPA" : "lighting"} engine`]]
    : basis === "fixed" ? [["fixed", "Fixed quantity per room"]]
    : placeable ? [["placed", "Count what I place on the canvas"], ["fixed", "Fixed quantity per room"]]
    : [["calculated", BASIS_BY_VALUE[basis]?.label || "Calculated"], ["fixed", "Fixed quantity per room"]];
  // A formula (Setup ▸ Estimate Engine ▸ Formulas) replaces the entity's
  // basis for a non-engine element — e.g. coving LF = perimeter - doorCount*3.
  if (picked && !engine && formulas.length) methods.push(["formula", "Formula (per-room expression)"]);
  const effMethod = method || methods[0]?.[0];
  const effFormulaId = formulaId ?? formulas[0]?.FormulaID ?? null;
  // Ungated preview: the engine hasn't been enabled for this room yet.
  const ungatedCtx = useMemo(() => ({ ...(systemsCtx || {}), roomElementsByRoom: undefined }), [systemsCtx]);
  const previewSystems = useMemo(() => picked ? calcRoomSystems(room, criteria || {}, ungatedCtx) : null, [picked, room, criteria, ungatedCtx]);
  const preview = picked ? resolveElementQuantity({
    element: { ...picked, QuantityMethod: effMethod === "formula" ? "calculated" : effMethod, QuantityFormulaID: effMethod === "formula" ? effFormulaId : null, FixedQty: effMethod === "fixed" ? (+fixedQty || 0) : null, OverrideQty: null },
    room, systems: previewSystems, placed: 0, formulas, criteria, ctx: ungatedCtx,
  }) : null;
  const payload = () => ({
    room_id: room.RoomID, kind: picked.Kind, item_id: picked.Kind === "item" ? picked.ItemID : null, assembly_id: picked.Kind === "assembly" ? picked.AssemblyID : null,
    // Added from the full catalog = the estimator's choice of group wins; the curated list already matches it.
    discipline: (showAll ? discipline.key : inferElementDiscipline(picked)) || discipline.key,
    quantity_method: effMethod === "formula" ? "calculated" : effMethod,
    quantity_formula_id: effMethod === "formula" ? effFormulaId : null,
    fixed_qty: effMethod === "fixed" ? (+fixedQty || 0) : null,
  });

  return (
    <div style={{ border: "1px solid #93c5fd", background: "var(--tint-blue)", borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Add {discipline.label} element</div>
      {!picked ? (
        <>
          <input autoFocus placeholder="Search elements…" value={query} onChange={e => setQuery(e.target.value)} style={cellInp} />
          {!flaggedAny
            ? <div style={warnStyle}>No catalog rows are flagged as room elements yet — showing the whole catalog. Flag rows (and set discipline / quantity basis) under Setup ▸ Estimate Engine ▸ Items or Assemblies.</div>
            : (
              <label style={{ fontSize: 11.5, display: "flex", alignItems: "center", gap: 6, color: "var(--text2)" }}
                title="The short list is the catalog rows flagged as room elements for this discipline. Any Item or Assembly can be added to a room — tick this to search all of them.">
                <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> Show the full catalog (any Item or Assembly)
              </label>
            )}
          <div style={{ maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            {candidates.map(e => (
              <button key={entityKeyOf(e)} onClick={() => setPicked(e)} style={{ textAlign: "left", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 6, padding: "6px 8px", cursor: "pointer", fontSize: 12, display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span><span style={{ fontFamily: "monospace", fontWeight: 600, marginRight: 6 }}>{e.Code || "—"}</span>{e.Name}</span>
                <span style={{ color: "var(--text3)", fontSize: 11, whiteSpace: "nowrap" }}>{(BASIS_BY_VALUE[inferElementBasis(e)]?.label || "").split(" (")[0]}{e.Kind === "assembly" ? " · assembly" : ""}</span>
              </button>
            ))}
            {!candidates.length && <div style={{ fontSize: 11.5, color: "var(--text3)" }}>No matching elements{existing.length ? " (already-added ones are hidden)" : ""}.</div>}
          </div>
          <div><button onClick={onCancel} style={linkBtn}>Cancel</button></div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{picked.Name} <span style={{ fontFamily: "monospace", fontWeight: 500, color: "var(--text3)", fontSize: 11 }}>{picked.Code}</span></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {methods.map(([m, label]) => (
              <label key={m} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="radio" name={`method-${entityKeyOf(picked)}`} checked={effMethod === m} onChange={() => setMethod(m)} /> {label}
              </label>
            ))}
            {effMethod === "fixed" && <input type="number" min={0} step="any" value={fixedQty} onChange={e => setFixedQty(e.target.value)} style={{ ...cellInp, width: 110 }} />}
            {effMethod === "formula" && (
              <select value={effFormulaId || ""} onChange={e => setFormulaId(e.target.value ? +e.target.value : null)} style={{ ...cellInp, maxWidth: 320 }}>
                {formulas.map(f => <option key={f.FormulaID} value={f.FormulaID}>{f.Code} — {f.Name}{f.Expression ? ` = ${f.Expression}` : ""}</option>)}
              </select>
            )}
          </div>
          {preview && (
            <div style={{ fontSize: 12 }}>
              <span style={{ color: "var(--text3)" }}>Quantity now: </span>
              <strong>{fmtQty(preview.effective, preview.unit)} {preview.unit}</strong>
              <span style={{ color: "var(--text3)" }}> · {preview.basisLabel}</span>
              {preview.warnings.filter(w => w.code !== "element_unplaced").map(w => <div key={w.code} style={{ ...warnStyle, marginTop: 4 }}>⚠️ {w.message}</div>)}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => onAdd(payload())} style={primaryBtn}>Add to room</button>
            {placeable && canPlace && <button onClick={() => onAddAndPlace(payload(), picked)} style={primaryBtn} title="Add, then click the canvas to place each one">Add &amp; Place</button>}
            <button onClick={() => { setPicked(null); setMethod(null); }} style={linkBtn}>← Back</button>
            <button onClick={onCancel} style={linkBtn}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

// One element on the room: effective quantity first, its basis, canvas
// placed-vs-required for placeable items, warnings, then details/override
// behind a disclosure (framework doc §12: override is secondary).
// Back-outs on an area element (Takeoff Canvas 2.0 D-2): the deduct polygons
// drawn against it, each with the SF actually inside the room, and the entry
// to trace another. Quantity standard (UI brief §8): Gross − Deductions =
// Calculated Net ± Override = Pricing — the card's headline is the effective;
// this row shows the deductions behind it, never only the final number.
function BackOutsRow({ backOuts, net, unit, roomPlaced, canEdit, drawing, onAdd, onDelete }) {
  const u = (unit || "sf").toUpperCase();
  const byId = new Map((net?.deductions || []).map(d => [d.id, d]));
  return (
    <div style={{ marginTop: 6, borderTop: "1px dashed var(--cs-border)", paddingTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ ...fld, marginBottom: 0 }}>Back-outs</span>
        {net
          ? <span style={{ fontSize: 11.5, color: "var(--text2)" }}>{fmtQty(net.gross, unit)} gross − {fmtQty(net.totalDeduction, unit)} = <strong>{fmtQty(net.calculatedNet, unit)} {u}</strong> net</span>
          : <span style={{ fontSize: 11.5, color: "var(--text3)" }}>none — the full area is carried</span>}
        {canEdit && onAdd && roomPlaced && (
          <button onClick={onAdd} disabled={drawing} style={{ ...smallBtn, marginLeft: "auto" }}
            title="Trace a polygon on the canvas this element is NOT applied to (equipment footprint, column, exclusion). Deducted only where it lies inside the room; overlaps deducted once.">
            {drawing ? "Tracing on canvas…" : "＋ Add Back-Out"}
          </button>
        )}
      </div>
      {backOuts.map(b => {
        const d = byId.get(b.id);
        return (
          <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, padding: "2px 0 0 8px" }}>
            <span style={{ color: "#b91c1c" }}>⛔</span>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.label}</span>
            <span style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {d && !d.skipped ? `−${fmtQty(d.insideArea, unit)} ${u}` : "—"}
              {d?.clipped && <span title={`${fmtQty(d.outsideArea, unit)} ${u} of this back-out lies outside the room and is not deducted`} style={{ color: "#b45309" }}> ⚠ clipped</span>}
              {d?.skipped && <span style={{ color: "#b45309" }}> ⚠ no area</span>}
            </span>
            {canEdit && onDelete && <span onClick={() => { if (window.confirm("Remove this back-out?")) onDelete(b.row); }} style={{ cursor: "pointer", color: "var(--text3)" }} title="Remove back-out">🗑</span>}
          </div>
        );
      })}
      {!roomPlaced && canEdit && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>Place the room on the canvas to draw back-outs.</div>}
    </div>
  );
}

// Openings on a wall-area element (Takeoff Canvas 2.0 D-3): every opening in
// this room's walls with its effective policy and the SF it deducts, plus the
// element's own default ("Deduct openings"). A per-opening policy is set on
// the opening itself (Condition Detail Flyout) — one home per fact.
function OpeningsRow({ net, unit, canEdit, onToggleDefault }) {
  const u = (unit || "sf").toUpperCase();
  const rows = net?.deductions || [];
  return (
    <div style={{ marginTop: 6, borderTop: "1px dashed var(--cs-border)", paddingTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ ...fld, marginBottom: 0 }}>Openings</span>
        {net && net.totalDeduction > 0
          ? <span style={{ fontSize: 11.5, color: "var(--text2)" }}>{fmtQty(net.gross, unit)} gross − {fmtQty(net.totalDeduction, unit)} = <strong>{fmtQty(net.calculatedNet, unit)} {u}</strong> net</span>
          : <span style={{ fontSize: 11.5, color: "var(--text3)" }}>{rows.length ? "none deducted — the full wall area is carried" : "no openings in this room's walls"}</span>}
        <label style={{ marginLeft: "auto", fontSize: 11.5, display: "flex", alignItems: "center", gap: 4, cursor: canEdit ? "pointer" : "default" }}
          title="This element's default for openings whose own policy is 'inherit'. Never a hard rule — each opening can override it in its own flyout.">
          <input type="checkbox" checked={!!net?.elementDefault} disabled={!canEdit} onChange={e => onToggleDefault(e.target.checked)} />
          Deduct openings by default
        </label>
      </div>
      {rows.map(d => (
        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, padding: "2px 0 0 8px" }}>
          <span>{d.effectivePolicy === "deduct" ? "🚪" : "▫"}</span>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {d.label}{d.widthFt && d.heightFt ? ` · ${d.widthFt} × ${d.heightFt} ft` : ""}
            {d.policy !== "inherit" && <span style={{ color: "var(--text3)" }}> · {d.policy === "deduct" ? "always deduct" : "never deduct"}</span>}
          </span>
          <span style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            {d.unsized ? <span style={{ color: "#b45309" }}>⚠ no width × height</span>
              : d.effectivePolicy === "deduct" ? `−${fmtQty(d.insideArea, unit)} ${u}`
              : <span style={{ color: "var(--text3)" }}>kept</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function ElementCard({ el, room, systems, systemsCtx, formulas = [], criteria, takeoffConditions, roomTemplates = [], roomTemplateEquipment = [], canEdit, onSave, onDelete, onPlace, onResetAutoLayout, onSaveCriterion, onAddBackOut, onDeleteBackOut, drawingBackOut = false, rooms = [], onHighlight, layoutReview = null, layoutReviewOn = false, onReviewLayout }) {
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState(false);
  const placed = placedCountFor(el, room.RoomID, takeoffConditions);
  const backOuts = backOutsForElement(el, takeoffConditions);
  const openings = openingsForRoom(room, takeoffConditions, rooms);
  const autoCount = takeoffConditions.filter(c => c.RoomID === room.RoomID && c.AutoSystem && c.Kind === el.Kind && (el.Kind === "item" ? c.ItemID === el.ItemID : c.AssemblyID === el.AssemblyID)).length;
  const q = resolveElementQuantity({ element: el, room, systems, placed, formulas, criteria, ctx: systemsCtx, backOuts, openings });
  const placeable = isPlaceable(el);
  // A formula on the element replaces the engine as its quantity source.
  const engine = isEngineBasis(q.basis) && !q.usesFormula;
  const system = !engine ? null : q.basis === "engine_hepa" ? "hepa" : q.basis === "engine_light" ? "light" : null;
  const roomPlaced = room.CanvasX != null && room.CanvasX !== "";
  const methodValue = q.usesFormula ? "formula" : q.method;
  // Template provenance (SourceTemplateEquipmentID, db/70): the row is a COPY
  // made at seed time — later edits to the template never reach it — so say
  // where it came from, and whether that template is still this room's.
  const seededFrom = el.SourceTemplateEquipmentID != null ? roomTemplateEquipment.find(t => t.RoomTemplateEquipmentID === el.SourceTemplateEquipmentID) : null;
  const seededTemplate = seededFrom ? roomTemplates.find(t => t.RoomTemplateID === seededFrom.RoomTemplateID) : null;
  const provenance = el.SourceTemplateEquipmentID == null ? null
    : !seededFrom ? "seeded from a template (row since removed from it)"
    : seededTemplate?.RoomTemplateID === room.RoomTemplateID ? `from template ${seededTemplate.Name}`
    : `from previous template ${seededTemplate?.Name || "—"}`;

  const save = (patch) => onSave({
    room_element_id: el.RoomElementID, room_id: el.RoomID, kind: el.Kind, item_id: el.ItemID, assembly_id: el.AssemblyID,
    discipline: el.Discipline, quantity_method: el.QuantityMethod, quantity_formula_id: el.QuantityFormulaID ?? null,
    fixed_qty: el.FixedQty, override_qty: el.OverrideQty,
    notes: el.Notes, display_order: el.DisplayOrder, ...patch,
  });
  const changeMethod = (v) => v === "formula"
    ? save({ quantity_method: "calculated", quantity_formula_id: el.QuantityFormulaID || formulas[0]?.FormulaID || null })
    : save({ quantity_method: v, quantity_formula_id: null });
  // Engine elements keep their override on the room's criteria row (one
  // store per quantity); everything else overrides on the element itself.
  const overrideCriterion = system === "hepa" ? ["mech", "hepa_count_override"] : system === "light" ? ["elec", "lighting_fixture_count"] : null;
  const overrideStored = overrideCriterion ? (criteria?.[`${overrideCriterion[0]}|${overrideCriterion[1]}|${room.RoomID}`] ?? "") : (el.OverrideQty ?? "");
  const commitOverride = (v) => {
    if (overrideCriterion) onSaveCriterion?.(overrideCriterion[0], overrideCriterion[1], room.RoomID, v);
    else save({ override_qty: v === "" ? null : +v });
  };

  return (
    <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, padding: "8px 10px", background: "var(--surface)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, display: "flex", gap: 6, alignItems: "baseline" }}>
            <span style={{ color: "#166534" }}>✓</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{el.Name || el.Code}</span>
            {el.Code && <span style={{ fontFamily: "monospace", fontWeight: 500, color: "var(--text3)", fontSize: 11 }}>{el.Code}</span>}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 2 }}>
            {q.basisLabel}{q.override != null ? " · manual override" : ""}
            {el.SyncsToCriteriaField && (
              <span title={`This element supersedes the legacy per-room "${SYNC_FIELD_OPTIONS.find(o => o.value === el.SyncsToCriteriaField)?.label || el.SyncsToCriteriaField}" count for its readers; that field is only the fallback for rooms without the element.`}>
                {" · "}{el.SyncsToCriteriaField === "outlet_count" ? "feeds receptacle circuits" : el.SyncsToCriteriaField.endsWith("_drops") ? "counts as this room's generic drops" : `replaces legacy ${SYNC_FIELD_OPTIONS.find(o => o.value === el.SyncsToCriteriaField)?.label || el.SyncsToCriteriaField} count`}
              </span>
            )}
            {provenance && <span title="Template provenance — a copy made when the room was seeded; later template edits don't change it. Remove or edit it here freely."> · {provenance}</span>}
          </div>
        </div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap", fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: q.override != null ? "#b45309" : "var(--text)" }}>
          {fmtQty(q.effective, q.unit)}<span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginLeft: 3 }}>{q.unit.toUpperCase()}</span>
        </div>
      </div>

      {engine && system && <GoverningRow system={system} sys={system === "hepa" ? systems?.hvac?.hepa : systems?.electrical?.lighting} />}
      {placeable && roomPlaced && (
        <div style={{ marginTop: 4 }}>
          <DrawnRow label="Placed on canvas" drawn={placed} autoCount={autoCount} effective={q.required} disabled={!canEdit}
            onAdopt={v => commitOverride(String(v))}
            onRegenerate={engine && onResetAutoLayout ? () => onResetAutoLayout(room, system) : null} />
          {engine && system && layoutReview?.systems?.[system] && (
            <LayoutReviewRow system={system} review={layoutReview} on={layoutReviewOn} onToggle={onReviewLayout ? () => onReviewLayout(room) : null} onHighlight={onHighlight} room={room} />
          )}
          {canEdit && onPlace && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
              <button onClick={() => onPlace(el)} style={smallBtn} title="Click the canvas to place one; stays in place mode for the next">＋ Place on canvas</button>
            </div>
          )}
        </div>
      )}
      {placeable && !roomPlaced && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Place the room on the canvas to position these.</div>}
      {BACK_OUT_BASES.has(q.basis) && !q.usesFormula && q.method === "calculated" && (
        <BackOutsRow backOuts={backOuts} net={q.net} unit={q.unit} roomPlaced={roomPlaced} canEdit={canEdit}
          drawing={drawingBackOut} onAdd={onAddBackOut ? () => onAddBackOut(el) : null} onDelete={onDeleteBackOut} />
      )}
      {q.basis === "wall_area" && !q.usesFormula && q.method === "calculated" && (
        <OpeningsRow net={q.net ?? { deductions: [], elementDefault: parseElementSettings(el).deductOpenings === true }} unit={q.unit} canEdit={canEdit}
          onToggleDefault={on => save({ settings_json: { ...parseElementSettings(el), deductOpenings: on } })} />
      )}

      {q.warnings.map(w => <div key={w.code} style={{ ...warnStyle, marginTop: 6 }}>⚠️ {w.message}</div>)}

      <div style={{ marginTop: 6, display: "flex", gap: 10, fontSize: 11 }}>
        <span onClick={() => setOpen(o => !o)} style={{ cursor: "pointer", color: "var(--cs-blue)" }}>{open ? "▾ Hide details" : "▸ Details & override"}</span>
        <span onClick={() => setWhy(w => !w)} style={{ cursor: "pointer", color: "var(--cs-blue)" }} title="Every step behind this quantity, and the geometry it came from">{why ? "▾ Hide why" : "Why this quantity?"}</span>
        {canEdit && <span onClick={() => { if (window.confirm(`Remove "${el.Name || el.Code}" from ${room.RoomName}?`)) onDelete?.(el.RoomElementID); }} style={{ cursor: "pointer", color: "var(--text3)", marginLeft: "auto" }}>Remove</span>}
      </div>

      {why && (() => {
        const t = quantityTrace(q, { element: el, room });
        return (
          <div style={{ marginTop: 6, borderTop: "1px dashed var(--cs-border)", paddingTop: 6, fontSize: 11.5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 700 }}>Why {fmtQty(q.effective, q.unit)} {q.unit.toUpperCase()}?</span>
              {onHighlight && roomPlaced && (
                <button onClick={() => onHighlight({ roomId: room.RoomID, conditionIds: t.sourceIds })} style={{ ...smallBtn, marginLeft: "auto" }} title="Select the room on the canvas and light up the back-outs / openings this quantity is built from">
                  Highlight geometry
                </button>
              )}
            </div>
            <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
              {t.lines.map(ln => (
                <tr key={ln.key}>
                  <td style={{ padding: "1px 6px 1px 0", color: "var(--text3)", whiteSpace: "nowrap", verticalAlign: "top" }}>{ln.label}</td>
                  <td style={{ padding: "1px 6px", fontWeight: ln.label === "Effective" ? 800 : 600, whiteSpace: "nowrap", verticalAlign: "top", fontVariantNumeric: "tabular-nums" }}>{ln.value}</td>
                  <td style={{ padding: "1px 0", color: "var(--text3)", verticalAlign: "top" }}>{ln.note || ""}</td>
                </tr>
              ))}
            </tbody></table>
            {t.warnings.map((w, i) => <div key={i} style={{ ...warnStyle, marginTop: 4 }}>⚠️ {w}</div>)}
            <div style={{ color: "var(--text3)", marginTop: 4 }}>Prices through {el.Code || el.Name} on the Estimate as {q.effective != null ? fmtQty(q.effective, q.unit) : "—"} {q.unit.toUpperCase()}{placeable ? " (placed symbols price through their own rows)" : ""}.</div>
          </div>
        );
      })()}
      {open && (
        <div style={{ marginTop: 6, borderTop: "1px dashed var(--cs-border)", paddingTop: 6 }}>
          {q.net ? (
            <>
              <EngRow label="Gross" value={fmtQty(q.net.gross, q.unit)} hint={q.basisLabel.replace(/ − \d+ (back-outs?|openings?)$/, "")} />
              <EngRow label={q.net.kind === "openings" ? `− Openings (${q.net.deductions.filter(d => d.effectivePolicy === "deduct" && !d.unsized).length} of ${q.net.deductions.length})` : `− Back-outs (${q.net.deductions.filter(d => !d.skipped).length})`}
                value={`−${fmtQty(q.net.totalDeduction, q.unit)}`}
                hint={q.net.kind === "openings" ? "each opening's own width × height, by its policy over the element default"
                  : q.net.overlapArea > 0 ? `inside the room only; ${fmtQty(q.net.overlapArea, q.unit)} of overlap deducted once` : "inside the room outline only"} />
              <EngRow label="Calculated net" value={fmtQty(q.calculated, q.unit)} />
            </>
          ) : <EngRow label="Calculated" value={fmtQty(q.calculated, q.unit)} hint={q.basisLabel} />}
          <OverrideRow label="Override" storedValue={overrideStored === "" ? "" : String(overrideStored)} calculated={q.calculated} disabled={!canEdit} onCommit={commitOverride} />
          <EngRow label="Effective" value={`${fmtQty(q.effective, q.unit)} ${q.unit}`} strong />
          {engine && q.storedMethod !== "calculated" && canEdit && (
            <div style={{ ...engRow, alignItems: "center" }}>
              <span style={engLbl}>Stored method "{q.storedMethod}"</span>
              <button onClick={() => save({ quantity_method: "calculated", fixed_qty: null })} style={smallBtn}
                title="Clear the stale method left by an older template seed. The engine already governs this count; this only removes the warning.">
                Reset to engine-calculated
              </button>
            </div>
          )}
          {!engine && canEdit && (
            <div style={{ ...engRow, alignItems: "center" }}>
              <span style={engLbl}>Quantity method</span>
              <select value={methodValue} onChange={e => changeMethod(e.target.value)} style={{ ...cellInp, width: 190 }}>
                {placeable && <option value="placed">Count placed symbols</option>}
                {!placeable && q.basis !== "fixed" && !isEngineBasis(q.basis) && <option value="calculated">{BASIS_BY_VALUE[q.basis]?.label}</option>}
                {isEngineBasis(q.basis) && <option value="calculated">{BASIS_BY_VALUE[q.basis]?.label}</option>}
                <option value="fixed">Fixed quantity per room</option>
                {(formulas.length > 0 || q.usesFormula) && <option value="formula">Formula (per-room expression)</option>}
              </select>
            </div>
          )}
          {!engine && q.usesFormula && (
            <div style={{ ...engRow, alignItems: "center" }}>
              <span style={engLbl}>Formula</span>
              {canEdit && formulas.length ? (
                <select value={el.QuantityFormulaID || ""} onChange={e => save({ quantity_formula_id: e.target.value ? +e.target.value : null })} style={{ ...cellInp, maxWidth: 300 }}>
                  {formulas.map(f => <option key={f.FormulaID} value={f.FormulaID}>{f.Code} — {f.Name}</option>)}
                </select>
              ) : <span style={{ fontFamily: "monospace", fontSize: 11 }}>{q.formula?.Code} = {q.formula?.Expression}</span>}
            </div>
          )}
          {!engine && q.method === "fixed" && (
            <div style={{ ...engRow, alignItems: "center" }}>
              <span style={engLbl}>Fixed quantity</span>
              <input key={`fx-${el.FixedQty ?? ""}`} type="number" min={0} step="any" defaultValue={el.FixedQty ?? ""} disabled={!canEdit} style={engInp}
                onBlur={e => { const v = e.target.value === "" ? null : +e.target.value; if (v !== (el.FixedQty ?? null)) save({ fixed_qty: v }); }} />
            </div>
          )}
          {system === "hepa" && <HepaEngineeringDetails hepa={systems.hvac.hepa} systems={systems} />}
          {system === "light" && <LightingEngineeringDetails lighting={systems.electrical.lighting} systems={systems} />}
        </div>
      )}
    </div>
  );
}

// One scope-list row's fields (description/qty/unit + revert/remove) — shared
// between grouped and "not yet cataloged" rendering below so the markup only
// lives once.
function ScopeRow({ row, isLocked, onSaveField, onRevert, onRemove }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", padding: "6px 8px" }}>
      <input
        type="color"
        title="Color"
        value={row.color || "#94a3b8"}
        disabled={isLocked}
        onChange={e => onSaveField(row, { color: e.target.value })}
        style={{ width: 18, height: 18, padding: 0, border: "1px solid var(--cs-border)", borderRadius: 3, cursor: isLocked ? "default" : "pointer", flexShrink: 0 }}
      />
      <input
        defaultValue={row.description}
        disabled={isLocked}
        style={{ ...cellInp, flex: 1 }}
        title={row.suggestedCsi ? `Suggested CSI ${row.suggestedCsi}` : ""}
        onBlur={e => { const v = e.target.value.trim(); if (v && v !== row.description) onSaveField(row, { description: v }); else e.target.value = row.description; }}
      />
      <input
        // Keyed on the value so an unedited row's input remounts (and
        // re-shows the freshly computed number) when L/W/H or a
        // criteria selection changes — a plain defaultValue only
        // applies once, on mount, and would otherwise display a
        // stale number forever even though the real value moved.
        key={`qty-${row.quantity}`}
        type="number" min={0} step="any"
        defaultValue={row.quantity}
        disabled={isLocked}
        style={{ ...cellInp, width: 72, textAlign: "right" }}
        onBlur={e => { const v = +e.target.value; if (v !== row.quantity) onSaveField(row, { quantity: v }); else e.target.value = row.quantity; }}
      />
      <input
        defaultValue={row.unit}
        disabled={isLocked}
        style={{ ...cellInp, width: 44, textAlign: "center" }}
        onBlur={e => { const v = e.target.value.trim() || row.unit; if (v !== row.unit) onSaveField(row, { unit: v }); else e.target.value = row.unit; }}
      />
      <select
        title="Pattern"
        value={row.pattern || ""}
        disabled={isLocked}
        onChange={e => onSaveField(row, { pattern: e.target.value || null })}
        style={{ ...cellInp, width: 58, flexShrink: 0, padding: "4px 2px", fontSize: 11 }}
      >
        {PATTERN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {row.isOverridden && !isLocked && (
        <button onClick={() => onRevert(row)} title="Edited — no longer tracks L/W/H or criteria changes. Click to revert to the computed value."
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#d97706", padding: "2px 3px", flexShrink: 0 }}>↺</button>
      )}
      {row.isOverridden && isLocked && (
        <span title="Edited — no longer tracks L/W/H or criteria changes" style={{ fontSize: 11, color: "#d97706", flexShrink: 0 }}>✎</span>
      )}
      {!isLocked && (
        <button onClick={() => onRemove(row)} title="Remove" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#dc2626", padding: "2px 4px" }}>✕</button>
      )}
    </div>
  );
}

// Preliminary architectural scope-and-quantity list: baseline rows derive
// live from the room's own L/W/H + Discipline Criteria selections (never
// persisted — see roomScopeFormulas.js), the estimator's edits/dismissals/
// custom additions layer on top via RoomScopeItems (db/39_room_scope_items.sql).
// Rows are grouped by cost code the same way Condition Library groups
// condition types — but unlike Condition Library, RoomScopeItems.CostCodeID
// is only ever set by an explicit CostCodePicker choice (db/39's own header
// comment: there's no safe way to auto-guess a live CostCodes row from
// takeoffCriteria.js's CSI hints), so a row with nothing picked yet — every
// baseline row until touched, since the baseline itself is never persisted —
// falls into an "Unassigned" bucket with an inline picker, mirroring
// Condition Library's "pick a cost code" section for a brand-new type.
function ArchitecturalScopeList({ room, criteria, roomScopeItems, roomElements = [], costCodes = [], isLocked, onSave, onDelete }) {
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ description: "", quantity: "", unit: "ea" });

  const allBaseline = useMemo(() => computeBaselineRoomScope(room, criteria), [room, criteria]);
  const forRoom = useMemo(() => roomScopeItems.filter(i => i.RoomID === room.RoomID), [roomScopeItems, room.RoomID]);
  const overrideBySourceKey = useMemo(() => {
    const m = new Map();
    for (const i of forRoom) if (i.SourceKey) m.set(i.SourceKey, i);
    return m;
  }, [forRoom]);
  // A derived line the room's real elements already cover is hidden (never listed twice) unless the
  // estimator has already edited it — an edited line is theirs, so it stays.
  const covered = useMemo(() => coveredBaselineKeys((roomElements || []).filter(e => e.RoomID === room.RoomID)), [roomElements, room.RoomID]);
  const baseline = useMemo(() => allBaseline.filter(b => !covered.has(b.sourceKey) || overrideBySourceKey.has(b.sourceKey)), [allBaseline, covered, overrideBySourceKey]);
  const hiddenByElements = allBaseline.length - baseline.length;
  const customRows = forRoom.filter(i => !i.SourceKey);

  const rows = [];
  const dismissed = [];
  for (const b of baseline) {
    const ov = overrideBySourceKey.get(b.sourceKey);
    if (ov?.IsRemoved) { dismissed.push({ sourceKey: b.sourceKey, description: b.description, roomScopeItemId: ov.RoomScopeItemID }); continue; }
    rows.push({
      key: b.sourceKey, sourceKey: b.sourceKey,
      roomScopeItemId: ov?.RoomScopeItemID ?? null,
      description: ov?.Description ?? b.description,
      unit: ov?.Unit ?? b.unit,
      quantity: ov ? ov.Quantity : b.quantity,
      costCodeId: ov?.CostCodeID ?? null,
      color: ov?.Color ?? null,
      pattern: ov?.Pattern ?? null,
      // Suggested only — takeoffCriteria.js's CSI hint, not a live CostCodes
      // match (see the header comment above). Shown to speed up the picker,
      // never used to auto-assign.
      suggestedCsi: b.csi,
      // An edited baseline row stops tracking L/W/H/criteria changes (its
      // stored override wins) until reverted — flagged so the UI can show
      // why a number that "should" be moving isn't.
      isOverridden: !!ov,
    });
  }
  for (const c of customRows) {
    rows.push({
      key: `custom-${c.RoomScopeItemID}`, sourceKey: null,
      roomScopeItemId: c.RoomScopeItemID,
      description: c.Description, unit: c.Unit, quantity: c.Quantity,
      costCodeId: c.CostCodeID ?? null, suggestedCsi: null,
      color: c.Color ?? null, pattern: c.Pattern ?? null,
    });
  }

  function saveField(row, patch) {
    onSave({
      roomScopeItemId: row.roomScopeItemId, roomId: room.RoomID, sourceKey: row.sourceKey,
      description: row.description, unit: row.unit, quantity: row.quantity,
      // Always resend the row's current cost code/color/pattern —
      // sp_SaveRoomScopeItem's UPDATE sets all three unconditionally, so
      // omitting one on an unrelated field edit would silently wipe it.
      costCodeId: row.costCodeId, color: row.color, pattern: row.pattern, ...patch,
    });
  }

  function removeRow(row) {
    if (!row.sourceKey) {
      // Fully custom line, no baseline to fall back to — delete outright.
      onDelete(row.roomScopeItemId);
      return;
    }
    // Baseline row: always DISMISS (upsert IsRemoved=1), never delete the
    // override outright — if one already exists (e.g. an edited quantity),
    // deleting it would just make the row reappear at its baseline value
    // instead of actually going away.
    onSave({ roomScopeItemId: row.roomScopeItemId, roomId: room.RoomID, sourceKey: row.sourceKey, costCodeId: row.costCodeId, color: row.color, pattern: row.pattern, description: row.description, unit: row.unit, quantity: 0, isRemoved: true });
  }

  // Undo an edit — deletes the override row entirely so the line goes back
  // to tracking L/W/H/criteria live, distinct from removeRow's "dismiss"
  // (which hides the line; this keeps it visible, just un-frozen).
  function revertRow(row) {
    if (row.roomScopeItemId) onDelete(row.roomScopeItemId);
  }

  function submitAdd() {
    const qty = +draft.quantity;
    if (!draft.description.trim() || !(qty > 0)) return;
    onSave({ roomId: room.RoomID, sourceKey: null, description: draft.description.trim(), unit: draft.unit || "ea", quantity: qty });
    setDraft({ description: "", quantity: "", unit: "ea" });
    setAddOpen(false);
  }

  return (
    <div style={{ borderTop: "1px solid var(--cs-border)", padding: "10px 20px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          🏗️ Architectural Scope &amp; Quantities
        </div>
        {!isLocked && (
          <button onClick={() => setAddOpen(o => !o)} style={{ fontSize: 11, fontWeight: 700, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            {addOpen ? "Cancel" : "+ Add line"}
          </button>
        )}
      </div>

      {rows.length === 0 && !addOpen && (
        <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>
          {hiddenByElements > 0
            ? "Everything derived for this room is already covered by its Room Build-Up elements above."
            : "Nothing derived for this room. Add ceiling, floor, coving, door elements under Room Build-Up above — they price; this list is only a legacy preview."}
        </div>
      )}
      {hiddenByElements > 0 && rows.length > 0 && (
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6 }}>
          {hiddenByElements} derived line{hiddenByElements === 1 ? "" : "s"} hidden — already covered by this room's Room Build-Up elements.
        </div>
      )}

      <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: rows.length > 0 ? 8 : 0, marginTop: rows.length === 0 && !addOpen ? 6 : 0 }}>
        Wall panel SF isn't generated here — a room can mix systems wall-by-wall (e.g. 2″ honeycomb on one wall, 1/2″ liner on another), which a single room-level value can't represent. Use <strong>Draw condition</strong> on the Layout Canvas to take off wall panel run-by-run instead.
      </div>

      {rows.length > 0 && (() => {
        const byCostCodeId = new Map();
        const unassigned = [];
        for (const row of rows) {
          const cc = row.costCodeId != null ? costCodes.find(c => c.CostCodeID === row.costCodeId) : null;
          if (cc) {
            if (!byCostCodeId.has(cc.CostCodeID)) byCostCodeId.set(cc.CostCodeID, { cc, rows: [] });
            byCostCodeId.get(cc.CostCodeID).rows.push(row);
          } else {
            unassigned.push(row);
          }
        }
        const groups = [...byCostCodeId.values()].sort((a, b) =>
          (a.cc.CSICodeNumeric || "").localeCompare(b.cc.CSICodeNumeric || "") || a.cc.CSICode.localeCompare(b.cc.CSICode)
        );

        return (
          <div style={{ border: "1px solid var(--cs-border)", borderRadius: 6, overflow: "hidden" }}>
            {groups.map((g, gi) => (
              <div key={g.cc.CostCodeID} style={{ borderTop: gi > 0 ? "1px solid var(--cs-border)" : "none" }}>
                <div style={{ padding: "6px 8px 2px", fontSize: 10, fontWeight: 700, color: "var(--text3)" }}>
                  <span style={{ fontFamily: "monospace", marginRight: 6 }}>{g.cc.CSICode}</span>{g.cc.Description}
                </div>
                {g.rows.map((row, i) => (
                  <div key={row.key} style={{
                    borderTop: i > 0 ? "1px solid var(--cs-border)" : "none",
                    background: i % 2 ? "var(--surface)" : "var(--surface2)",
                  }}>
                    <ScopeRow row={row} isLocked={isLocked} onSaveField={saveField} onRevert={revertRow} onRemove={removeRow} />
                  </div>
                ))}
              </div>
            ))}
            {unassigned.length > 0 && (
              <div style={{ borderTop: groups.length > 0 ? "1px solid var(--cs-border)" : "none" }}>
                <div style={{ padding: "6px 8px 2px", fontSize: 10, fontWeight: 700, color: "#d97706" }}>
                  Unassigned — pick a cost code
                </div>
                {unassigned.map((row, i) => (
                  <div key={row.key} style={{
                    borderTop: i > 0 ? "1px solid var(--cs-border)" : "none",
                    background: i % 2 ? "var(--surface)" : "var(--surface2)",
                  }}>
                    <ScopeRow row={row} isLocked={isLocked} onSaveField={saveField} onRevert={revertRow} onRemove={removeRow} />
                    <div style={{ padding: "0 8px 6px", display: "flex", alignItems: "center", gap: 8 }}>
                      {isLocked ? (
                        <span style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic" }}>No cost code assigned</span>
                      ) : (
                        <>
                          <div style={{ flex: 1, maxWidth: 260 }}>
                            <CostCodePicker
                              value={row.costCodeId}
                              costCodes={costCodes}
                              onSelect={cc => saveField(row, { costCodeId: cc.CostCodeID })}
                            />
                          </div>
                          {row.suggestedCsi && (
                            <span style={{ fontSize: 10.5, color: "var(--text3)" }}>Suggested: {row.suggestedCsi}</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {addOpen && !isLocked && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: rows.length > 0 ? 8 : 0 }}>
          <input
            placeholder="Description"
            value={draft.description}
            onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            style={{ ...cellInp, flex: 1 }}
          />
          <input
            type="number" min={0} step="any"
            placeholder="Qty"
            value={draft.quantity}
            onChange={e => setDraft(d => ({ ...d, quantity: e.target.value }))}
            style={{ ...cellInp, width: 72, textAlign: "right" }}
          />
          <input
            placeholder="Unit"
            value={draft.unit}
            onChange={e => setDraft(d => ({ ...d, unit: e.target.value }))}
            style={{ ...cellInp, width: 44, textAlign: "center" }}
          />
          <button onClick={submitAdd} title="Add" style={{ background: "var(--cs-blue)", border: "none", borderRadius: 5, cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 700, padding: "4px 10px" }}>Add</button>
        </div>
      )}

      {dismissed.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 10.5, color: "var(--text3)" }}>
          {dismissed.length} dismissed —{" "}
          {dismissed.map((d, i) => (
            <span key={d.sourceKey}>
              {i > 0 && " · "}
              <button
                onClick={() => onDelete(d.roomScopeItemId)}
                disabled={isLocked}
                title="Restore"
                style={{ background: "none", border: "none", cursor: isLocked ? "default" : "pointer", color: "var(--text3)", textDecoration: "underline", fontSize: 10.5, padding: 0 }}
              >
                {d.description}
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
